const journeys = {
  journeyWebsite: {
    skipToContent: "Skip to main content",
    price: {
      originalPriceLabel: "Original price {{price}}",
    },
    header: {
      tagline: "WHOLE-PERSON CARE",
      startTesting: "Start testing",
      navHowItWorks: "How it works",
      navJourneys: "Journeys",
      navAbout: "About",
      exploreProducts: "Explore Products",
      exploreLabs: "Explore Labs",
      navigation: "Journeys website navigation",
      storeNameGoHome: "{{storeName}}, go to homepage",
      goHome: "Go to homepage",
    },
    faq: {
      title: "FAQ",
      subtitle:
        "Find answers to your questions about our personalized wellness programs and services.",
      labsSubtitle: "Answers to your questions about lab testing and services.",
      whatIncludedQuestion: 'What does a "Journey" include?',
      shoppableWhatIncludedQuestion: "What does a Journey include?",
      whatIncludedAnswer:
        "A Journey is a personalized wellness plan that goes beyond standard lab work. You'll receive comprehensive lab testing, detailed interpretations, a provider-crafted wellness plan covering nutrition, movement, stress management, and targeted supplements to meet your goals. With follow-up testing and reminders built in to keep you on track, everything lives in one place, so staying on top of your health is easy.",
      hsaFsaQuestion: "Is this HSA and FSA Eligible?",
      shoppableHsaFsaQuestion: "Is my Journey HSA and FSA Eligible?",
      hsaFsaAnswer:
        "With Journeys everything is covered in one transparent price with no hidden fees or surprise costs. Many HSA and FSA accounts can be used to cover the cost, but please check with your plan administrator for eligibility.",
      differenceQuestion:
        "What makes Journeys different from a regular checkup or wellness program?",
      differenceAnswer:
        "A regular checkup is designed to test baseline health markers and flag problems once they've already developed. Journeys takes a proactive, preventative approach — testing a broader set of biomarkers to catch early signs of imbalances before they become bigger issues. It's built for people who want to take control of their health and live healthier, longer.",
      optimalRangesQuestion:
        "What's the difference between functional optimal ranges and conventional lab ranges?",
      optimalRangesAnswer:
        'Conventional lab ranges are designed to identify disease — if your results fall within the "normal" range, you\'re typically told everything looks fine. Functional optimal ranges are narrower and based on clinical research tied to long-term health outcomes. This means your results may flag areas for improvement that a standard lab test would miss, giving you a chance to act earlier.',
      schedulingQuestion: "How do I schedule my lab tests?",
      schedulingAnswer:
        "After purchasing your lab test, you'll receive an email with clear instructions and a link to schedule your appointment. We've partnered with Quest Diagnostics, offering over 2,000 locations nationwide, making it easy to coordinate your blood draw.",
      fullscriptQuestion: "What is Fullscript?",
      fullscriptAnswer:
        "Fullscript is trusted by over 125,000 providers and is the platform that powers your Journey. It's where your provider manages your care end-to-end — from ordering your lab tests and crafting your wellness plan, to sending you updates, appointment reminders, and alerts when your results are ready.",
      turnaroundQuestion: "How long does it take to get my results back?",
      turnaroundAnswer:
        "Turnaround times vary by lab. They're calculated from the date your sample is received at the lab. You can view a full list of turnaround times by individual lab company in Fullscript. If you have any questions about your sample, you can reach us at labs@fullscript.com.",
      viewResultsQuestion: "Where can I view my results?",
      viewResultsAnswer:
        "Results will be automatically released to you via email once they become available. If you have any questions about receiving results, you can always message our team through Fullscript at labs@fullscript.com.",
      paymentMethodsQuestion: "What payment methods are accepted?",
      paymentMethodsAnswer:
        "You can pay for your order using all major credit and debit cards. At this time, insurance reimbursement is not available for these tests. If you have any questions about completing your payment, you can reach our team at labs@fullscript.com.",
    },
    shareSection: {
      title: "Share ",
      titleItalic: "Journeys",
      subtitle: "Invite friends and family to start their Journey today!",
      bulletInterpretations: "Provider-reviewed interpretations",
      bulletRanges: "Functional optimal ranges",
      bulletNoSubscription: "No subscription required",
      bulletHsaFsa: "HSA/FSA eligible",
      sendInvite: "Send invite",
      emailSubject: "Start your health Journey on Fullscript",
      emailBodyPrefix:
        "I wanted to share these health Journeys with you. Check them out and get started here: ",
      shareTitle: "Start your health Journey on Fullscript",
    },
    howItWorks: {
      eyebrow: "The process",
      title: "How it works",
      step01Label: "Step 01",
      step01Title: "Book your lab test",
      step01Description:
        "Choose a panel and complete a brief health intake form. We'll help coordinate your lab appointment at a nearby partner clinic.",
      step02Label: "Step 02",
      step02Title: "Get your results interpreted",
      step02Description:
        "We'll analyze your results using functional optimal ranges instead of conventional reference ranges that catch problems but miss the early warning signs.",
      step03Label: "Step 03",
      step03Title: "Follow your wellness plan",
      step03Description:
        "Receive a personalized wellness plan with actionable steps across nutrition, movement, supplements, and stress management, built around your goals.",
    },
    shoppableHowItWorks: {
      journeysStep1Title: "Book lab test",
      journeysStep1Description:
        "Order your lab panel focused on the areas that matter most to you. We'll help coordinate your lab appointment.",
      journeysStep1ImageAlt: "Practitioner preparing a patient's arm for a blood draw",
      journeysStep2Title: "Get results interpreted",
      journeysStep2Description:
        "{{- storeName}} will explain what's in range, what's not, and how it impacts your health, giving you clarity and confidence to act.",
      journeysStep2ImageAlt: "Hand holding a phone showing an interpreted lab result",
      journeysStep3Title: "Follow your plan",
      journeysStep3Description:
        "You'll get a personalized wellness plan built around your results. This can include supplements, nutrition, and lifestyle changes.",
      journeysStep3ImageAlt: "Supplements laid out on a neutral background",
      journeysStep4Title: "Track progress",
      journeysStep4Description:
        "Re-test to give you and your provider a clear picture of what's working as well as what to adjust.",
      journeysStep4ImageAlt: "Person reviewing their wellness plan on a laptop at home",
      labsStep1Title: "Order your labs online",
      labsStep1Description: "Select your lab and check out",
      labsStep2Title: "Get your testing instructions",
      labsStep2Description:
        "Receive personalized instructions to complete your test at a lab location or at home",
      labsStep3Title: "Complete your test",
      labsStep3Description:
        "Complete your sample collection at a lab or at home, depending on your test type",
      labsStep4Title: "Receive your results",
      labsStep4Description:
        "Access your results in the Fullscript app, with a support team available to help",
    },
    hero: {
      eyebrow: "Functional Health Journeys",
      headline: "No one should navigate their health alone",
      subheadline:
        "With {{-pracName}}, you get more than lab results — you get a clear path forward. Every Journey combines advanced lab testing with a provider-reviewed wellness plan personalized to your results, giving you real direction on reaching your goals.",
      sentBy: "Sent by {{-pracName}}",
      startTesting: "Start testing",
      badgeClinical: "Clinically grounded",
      badgeProvider: "Provider-reviewed",
      badgeHsaFsa: "HSA/FSA eligible",
    },
    shoppableLabsHero: {
      headline: "Get clarity about your health",
      descriptionJourneys:
        "With <bold>{{- storeName}}</bold>, you get a clear path forward. Every panel pairs advanced testing with a provider-reviewed plan personalized for you.",
      descriptionLabs:
        "Choose from a wide range of lab testing options, including routine tests and specialty kits.",
      descriptionBoth:
        "Whether you need quick answers or an ongoing plan, <bold>{{- storeName}}</bold> combines advanced lab testing with provider-reviewed guidance, on your terms.",
      journeyStartingPrice:
        "Start your Journey with {{- storeName}} for only <bold>{{- price}}</bold>.",
      startTesting: "Start testing",
      exploreJourneys: "Explore Journeys",
      exploreLabTests: "Explore lab tests",
      badgeClinical: "Clinically grounded",
      badgeProvider: "Provider-reviewed",
      badgeNoHiddenCosts: "No hidden costs",
      badgeNoInsuranceRequired: "No insurance required",
      badgeHsaFsa: "HSA/FSA eligible",
      journeysQuote:
        "“For the first time ever I can see the full picture of where I’m at — and a plan forward.”",
      journeysQuoteAttribution: "Jill S • Journeys client",
      labsQuote:
        "“The testing process was truly seamless and almost half of what I pay out of pocket elsewhere.”",
      labsQuoteAttribution: "Stephanie H • Client",
    },
    comparison: {
      title: "Both start with a lab test. The difference is what happens with your results.",
      labsName: "Lab tests",
      labsDescription:
        "One-time answer, ordered on your own. Results delivered fast, provider-reviewed accuracy.",
      labsBestFor: "Best if you know what you're checking for.",
      labsCta: "Explore lab tests",
      journeysName: "Journey",
      journeysDescription:
        "Test plus an ongoing plan built around your results, with milestones like re-testing or a supplement plan.",
      journeysBestFor: "Best if you want a clear next step.",
      journeysCta: "Explore Journeys",
    },
    panelGrid: {
      eyebrow: "All Journeys ({{count}})",
      title: "Built for <italic>you</italic>",
      subtitle:
        "Whether you're trying to understand your baseline or dive deep into a specific health area, we have the right lab panel for you.",
      sectionMenuAriaLabel: "Journey categories",
      showMore: "Show more ({{count}})",
      biomarkers: "{{count}} biomarkers",
      benefitInterpreted: "Results interpreted by your provider",
      benefitLocations: "2,000+ Quest blood draw locations",
      benefitPlan: "Personalized wellness plan",
      learnMore: "Learn more",
      learnMoreLabel: "Learn more — {{-journeyName}}",
      buyNow: "Buy now",
      buyNowLabel: "Buy now — {{-journeyName}}",
      viewPanel: "View panel",
      checkout: "Checkout",
      signatureTag: "Based on Dr. Bland's methodology",
    },
    labProducts: {
      title: "Lab tests",
      sectionMenuAriaLabel: "Lab test categories",
      biomarkers: "{{count}} biomarker",
      biomarkers_plural: "{{count}} biomarkers",
      learnMore: "Learn more",
      learnMoreLabel: "Learn more — {{-productName}}",
      buyNow: "Buy now",
      buyNowLabel: "Buy now — {{-productName}}",
      showMore: "Show more ({{count}})",
    },
    providerSection: {
      eyebrow: "Your practitioner",
      photoAlt: "Photo of {{-pracName}}",
    },
    valueProp: {
      eyebrow: "About",
      title: "Care that looks for the <italic>why</italic>, not just the <italic>what</italic>",
      bodyParagraph1:
        "Conventional lab panels are designed to detect disease. Our panels are designed to optimize health. There's a meaningful difference — and it starts with testing the right things, in the right context, and interpreting them through a functional lens.",
      bodyParagraph2:
        "Every journey is provider-reviewed, clinically grounded, and delivered in plain language. You get more than data, you get a clear plan of action built around your biology.",
      proofClinicalTitle: "Clinically grounded",
      proofClinicalDescription: "Every biomarker is tied to clinical relevance",
      proofRangesTitle: "Optimal ranges",
      proofRangesDescription: "Catch what conventional labs miss",
      proofReviewedTitle: "Provider-reviewed",
      proofReviewedDescription: "Human interpretation across every result",
      proofActionTitle: "Action-oriented",
      proofActionDescription: "A plan that gives you what to do next",
      lifestyleImageAlt: "Woman sitting on a couch checking her wellness plan on her phone",
      notificationTitle: "New message from {{-pracName}}:",
      notificationBody: "It's time to book your follow-up lab, and track your progress!",
      notificationTimestamp: "2m ago",
    },
    pdp: {
      breadcrumb: "All Journeys",
      biomarkersIncluded: "{{count}} biomarkers included",
      checkout: "Checkout",
      allInclusive: "All-inclusive · No hidden fees",
      sidebar: {
        signatureTag: "Based on Dr. Bland's methodology",
        bulletLabDraw: "Blood draw at local Quest lab",
        bulletBiomarkers: "~{{count}} biomarker analysis",
        bulletPlan: "Personalized wellness plan",
        bulletFollowUp: "Follow-up support and check-ins",
        howItWorksLabel: "How it works",
        step1Number: "1.",
        step1Title: "Book & prepare",
        step1Description:
          "After intake and lab booking, you'll visit one of our partner lab clinics for your blood draw.",
        step2Number: "2.",
        step2Title: "Receive your results and plan",
        step2Description:
          "Within about a week you'll receive a detailed interpretation and clear plan.",
        step3Number: "3.",
        step3Title: "Follow your plan",
        step3Description:
          "Follow a personalized wellness plan with actionable steps across key health areas. Retest to track your progress against your health goals.",
        trustBiomarkers: "Biomarkers selected on clinical relevance",
        trustRanges: "Functional optimal ranges to catch early warning signs",
        trustInterpretation: "Human interpretation across every result with your trusted provider",
        trustNoSubscription: "No subscription required",
        phoneMockupAlt: "Wellness plan shown on a phone screen",
      },
      whatsTested: {
        eyebrow: "What's tested",
        title: "{{count}} biomarkers",
        shoppableTitle: "What we test",
        markers: "{{count}} marker",
        markers_plural: "{{count}} markers",
      },
      whatToExpect: {
        eyebrow: "Typical Journey timeline",
        title: "What to expect",
        day0Timeframe: "Day 0",
        day0Title: "Lab booking & intake",
        day0Description:
          "Complete your {{patientTerm}} intake and schedule your blood draw appointment",
        day1to5Timeframe: "Day 1-5",
        day1to5Title: "Lab test",
        day1to5Description: "Visit a partner lab clinic for your blood draw",
        day7to10Timeframe: "Day 7-10",
        day7to10Title: "Results ready",
        day7to10Description: "Your provider reviews and analyzes using functional optimal ranges",
        week2Timeframe: "Week 2",
        week2Title: "Wellness plan delivered",
        week2Description: "Personalized protocol across six pillars of functional health",
        month3to6Timeframe: "Month 3-6",
        month3to6Title: "Personalized follow-up",
        month3to6Description: "Check in on progress against your identified goals",
      },
    },
    labPdp: {
      breadcrumb: "Back",
      breadcrumbLabel: "Breadcrumb",
      specimenTags: {
        phlebotomy: "Phlebotomy",
        fastingRequired: "Fasting required",
        atHomeKit: "At-home kit",
      },
      sampleReport: {
        title: "Get a <italic>free</italic> sample report",
        description:
          "A clear, color-coded report your practitioner uses to build your plan. Every marker is grouped, flagged, and plotted against reference ranges.",
        bulletGrouped: "Every marker grouped by health area",
        bulletFlagged: "Color-coded flags with reference ranges on every marker",
        bulletExplained: "Plain-language explanation of what each result means",
        bulletReviewed: "Reviewed with you by the practitioner who curated this store",
        linkLabel: "View a free sample report",
        shoppableLinkLabel: "View sample report",
      },
      video: {
        eyebrow: "Video",
        title: "Collecting your sample",
        description:
          "No clinic visit needed. Watch exactly how the at-home kit works — from unboxing to dropping off your prepaid return package.",
        descriptionPhlebotomy:
          "Watch how the at-home kit works, then complete a blood draw at a partner lab. After purchasing, you'll receive instructions for that visit.",
      },
      testsIncluded: {
        title: "Test included",
        title_plural: "Tests included",
        biomarkers: "{{count}} biomarker",
        biomarkers_plural: "{{count}} biomarkers",
      },
      faq: {
        title: "FAQ",
        subtitle: "Answers to your questions about lab testing and services.",
      },
    },
    priceCard: {
      buyNow: "Buy now",
      oneTimePayment: "One-time payment — starts your first care cycle. No subscription.",
      header: {
        singleTest: "Lab test",
        panel: "Lab panel",
        specialtyKit: "Specialty kit",
        journey: "Journeys Program",
      },
      collectionMethod: {
        legend: "Collection method",
        partnerLabLabel: "Partner lab",
        partnerLabDescription: "Visit a lab near you",
        inOfficeLabel: "In-office",
        inOfficeDescription: "Draw at our clinic",
        comingSoon: "Coming soon",
      },
      feeLines: {
        labFee: "Lab fee",
        shipping: "Shipping",
      },
      additionalInfo: {
        hsaFsaEligible: "HSA/FSA eligible",
        shippingPolicy: "Shipping policy",
      },
    },
    pdpFaq: {
      eyebrow: "Common Questions",
      title: "FAQ",
      shoppableSubtitle: "Answers to common questions about panels, results, and your plan.",
      resultsTimeQuestion: "When will I receive my results?",
      resultsTimeAnswer:
        "You'll receive an email once your results are ready, typically within 10 days. Results include an explanation across each health area, so you can understand your full health picture, not just numbers on a page.",
      wellnessPlanQuestion: "What's included in my wellness plan?",
      wellnessPlanAnswer:
        "Your wellness plan includes an easy-to-read explanation of your lab results, plus recommendations across nutrition, exercise, stress management, and high quality supplements. Everything you need to take control of your health and reach your goals.",
      supplementsQuestion: "How do I order my supplements?",
      supplementsAnswer:
        "If your plan includes supplements to help bring your markers back into range, you can order them directly from your wellness plan.",
      afterPlanQuestion: "What happens after I finish my plan?",
      afterPlanAnswer:
        "Each Journey comes with built-in next steps, so you know what to expect. Once your plan is finished, you'll receive a notification — whether it's re-testing key markers, an updated supplement recommendation, or a follow-up appointment to review your progress. It's your chance to see how far you've come and map out what's next.",
      bloodDrawQuestion: "Where do I go for my blood draw?",
      bloodDrawAnswer:
        "Blood draws are completed at Quest Diagnostics locations. There are over 2,000 locations across the U.S. — most within 10 miles of your home. After you purchase your test, you can book a blood draw at the location that works best for you.",
    },
    sectionError: {
      message: "This section couldn't be loaded. Please try refreshing the page.",
    },
    footer: {
      description:
        "Health Journeys designed by {{-clinicName}}. Testing, interpretation, and a personalized plan — all in one.",
      explore: "Explore",
      browseJourneys: "Browse Journeys",
      howItWorks: "How It Works",
      company: "Company",
      privacy: "Privacy",
      copyright: "© {{year}} {{-clinicName}}. All rights reserved.",
    },
  },
  journeyOfferPage: {
    journeyWith: "Journey with {{-pracName}}",
    title: "The first step on your personal health Journey",
    description:
      "Comprehensive blood panels with a personalized interpretation and action plan to help you live healthier, longer.",
    biomarkers: "<strong>{{count}}</strong> biomarker",
    biomarkers_plural: "<strong>{{count}}</strong> biomarkers",
    personalizedPlan:
      "A personalized plan created by <strong>{{-pracName}}</strong> that evolves with you",
    lifetimeAccess: "<strong>Lifetime access</strong> to results tracked over time",
    hsaEligible: "HSA/FSA eligible",
    oneTimeFee: "One time fee no hidden costs",
    getStarted: "Get started",
    biomarkersPopover: {
      title: "Biomarkers included",
    },
    dontWait: "Don't wait. Starting your preventative Journey is simple",
    howItWorks: "How it works",
    labTesting: "Lab testing",
    labTestingDescription:
      "Order your lab panel focused on the areas that matter most. No waiting rooms. No prescription needed. Just real data, fast.",
    resultsExplainedStep: "Results, clearly explained",
    resultsExplainedDescriptionStep:
      "You'll be able to view and track your results, see what's in range, what's not, and what it means for your health. An expert practitioner translates the data into plain language and clear insights. ",
    personalizedPlanStep: "Personalized Wellness Plan",
    personalizedPlanDescriptionStep:
      "You'll get a personalized wellness plan built around your results. This can include supplements, nutrition, and lifestyle changes. Designed by your practitioner to be realistic, actionable, and effective.",
    retestProgressStep: "Re-test and track progress",
    retestProgressDescriptionStep:
      "Health isn't static, and neither is your plan. Re-testing gives you and your practitioner a clear picture of what's working and what to adjust, so your progress stays on track.",
    footerTitle:
      "This is the first step toward understanding your health more deeply — and taking control, <italic>together with {{-pracName}}</italic>",
  },
  JourneysList: {
    title: "Journeys Assistant",
    copyWebsiteUrl: "Copy website URL",
    invitePatient: "Invite {{patientTerm}} to a Journey",
    manage: "Manage",
    editJourneyPanels: "Edit journey panels",
    viewWebsite: "View website",
    previewJourneysExperience: "Preview the journeys experience",
    ordersMovedTitle: "Journeys orders moved to the Lab orders page",
    ordersMovedDescription:
      "Now you can track all labs in one place, including labs sent as a Journey.",
    viewLabOrders: "View lab orders",
  },
  bulkSend: {
    ctaLabel: "Notify {{count}} {{patientTerm}}",
    ctaLabel_plural: "Notify {{count}} {{patientTermPlural}}",
    ctaLabelEmpty: "No {{patientTermPlural}} to notify",
    bannerPrompt: "Ready to share Journeys with your {{patientTermPlural}}?",
    bannerCtaLabel: "Notify your base",
    newFeature: "New feature",
    modalTitle: "Let your {{patientTermPlural}} know about your Journeys",
    modalBody:
      "You can now send your journeys panels to your {{patientTerm}} list, in one click. You'll be notifying {{formattedCount}} {{patientTerm}} with this message.",
    modalBody_plural:
      "You can now send your journeys panels to your {{patientTerm}} list, in one click. You'll be notifying {{formattedCount}} {{patientTermPlural}} with this message.",
    sendToAll: "Send to all {{patientTermPlural}}",
    cancel: "Not now",
    confirmSend: "Yes, send to {{count}} {{patientTerm}}",
    confirmSend_plural: "Yes, send to all {{count}} {{patientTermPlural}}",
    confirmCancel: "Go back",
    success: "Invitation sent to {{count}} {{patientTerm}}",
    success_plural: "Invitations sent to {{count}} {{patientTermPlural}}",
    error: "We couldn't send your storefront right now. Please try again.",
  },
  inOfficeActivation: {
    newFeature: "New feature",
    modalTitle: "Journeys now supports Quest in-office phlebotomy",
    modalBody:
      "You can now run Quest in-office phlebotomy as part of a Journey. Fullscript drafts the interpretation and wellness plan when results arrive. Set up your Journey panels to get started.",
    cancel: "Not now",
    confirm: "Set up Journey panels",
  },
  JourneysFloatingBar: {
    patientDetailsButtonText: "Next",
    interpretationDetailsBuildPlan: "Build Wellness Plan",
    interpretationDetailsReviewPlan: "Review Wellness Plan",
    patientDetailsTextPrimary: "Next: Review Lab Results and Interpretation",
    patientDetailsTextSecondary:
      "The lab interpretation will <strong>not</strong> be shared with the {{patientTerm}} yet",
    interpretationDetailsTextPrimary: "Next: Review Wellness Plan",
    interpretationDetailsNotYetShared:
      "The lab interpretation will <strong>not</strong> be shared with the {{patientTerm}} yet",
    interpretationDetailsAlreadyShared:
      "The lab interpretation has already been shared with the {{patientTerm}}",
  },
  reminder: {
    successEmailSent: "Reminder email sent",
  },
  WomensHormones: {
    menopauseStatus: {
      pre: "Pre-menopause",
      peri: "Peri-menopause",
      post: "Post-menopause",
      unknown: "Unknown",
    },
    periodStatus: {
      regular: "Regular",
      irregular: "Irregular",
      no_period: "No period",
      unknown: "Unknown",
    },
    menstrualCyclePhase: {
      follicular: "Follicular Phase",
      luteal: "Luteal Phase",
      unknown: "Unknown",
    },
    WomensHormonesRangeUsed: "Reference range used (based on women's hormones survey)",
  },
};

export { journeys };
